import { formatDate } from './date'

interface DisplayReferenceConfig {
  shipmentDisplayField: string | null
  shipmentDisplayMode: 'replace' | 'complement'
}

function getNestedValue(obj: any, path: string): any {
  return path.split('.').reduce((current, key) => current?.[key], obj)
}

function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, c) => c.toUpperCase())
}

export const REFERENCE_FALLBACKS: Record<string, string[]> = {
  sales_order: ['salesOrderNumber', 'customerReferenceNumber'],
  salesOrder: ['salesOrderNumber', 'customerReferenceNumber'],
  salesOrderNumber: ['sales_order', 'customerReferenceNumber'],
  customerReferenceNumber: ['sales_order', 'salesOrderNumber'],
}

function hasValue(v: any): boolean {
  return v !== null && v !== undefined && v !== ''
}

function getValueWithFallbacks(obj: any, field: string): any {
  const direct = getNestedValue(obj, field)
  if (hasValue(direct)) return direct

  const camelField = snakeToCamel(field)
  if (camelField !== field) {
    const camelValue = getNestedValue(obj, camelField)
    if (hasValue(camelValue)) return camelValue
  }

  const fallbacks = REFERENCE_FALLBACKS[field]
  if (fallbacks) {
    for (const fb of fallbacks) {
      const val = getNestedValue(obj, fb)
      if (hasValue(val)) return val
    }
  }

  return direct
}

function formatValue(value: any): string | null {
  if (value === null || value === undefined || value === '') return null

  if (value.toDate && typeof value.toDate === 'function') {
    return formatDate(value.toDate())
  }

  if (value instanceof Date) {
    return formatDate(value)
  }

  if (typeof value === 'number') {
    return String(value)
  }

  if (typeof value === 'string') {
    return value
  }

  if (Array.isArray(value)) {
    return value.length > 0 ? formatValue(value[0]) : null
  }

  return null
}

export function resolveDisplayReference(
  shipment: any,
  config: DisplayReferenceConfig
): string {
  const fallback = shipment.idNumber || ''

  if (!config.shipmentDisplayField) return fallback

  const rawValue = getValueWithFallbacks(shipment, config.shipmentDisplayField)
  const formatted = formatValue(rawValue)

  return formatted || fallback
}
